import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiX, FiUsers, FiZap, FiEdit, FiBrand, FiClock, FiMessageCircle, FiVideo, FiSettings, FiCreditCard, FiBarChart } = FiIcons;

const Problems = () => {
  const problems = [
    {
      icon: FiUsers,
      title: "Limited webinar slots and attendee caps",
      description: "Restricts growth potential and forces expensive upgrades"
    },
    {
      icon: FiZap,
      title: "No AI automation for evergreen content",
      description: "Manual processes waste time and reduce personalization"
    },
    {
      icon: FiEdit,
      title: "No built-in editing for recorded webinars",
      description: "Hard to polish content without expensive external tools"
    },
    {
      icon: FiBrand,
      title: "Generic registration pages",
      description: "Poor brand alignment leads to low conversion rates"
    },
    {
      icon: FiClock,
      title: "No customizable thank-you experience",
      description: "Loses momentum after sign-up, reducing show rates"
    },
    {
      icon: FiMessageCircle,
      title: "Weak chat automation",
      description: "Missed engagement opportunities during live sessions"
    },
    {
      icon: FiVideo,
      title: "Poor video quality and streaming issues",
      description: "Buffering and drops harm credibility and conversions"
    },
    {
      icon: FiSettings,
      title: "No unified control room",
      description: "Difficult to manage Q&A, polls, and resource sharing"
    },
    {
      icon: FiCreditCard,
      title: "Confusing billing and plan limitations",
      description: "Forces unwanted upgrades or causes customer churn"
    },
    {
      icon: FiBarChart,
      title: "Bad analytics and tracking",
      description: "Cannot optimize conversions or measure ROI effectively"
    }
  ];

  return (
    <section className="py-20 gradient-bg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="font-serif text-4xl md:text-5xl font-bold mb-6 text-gray-900">
            Why normal webinar platforms 
            <span className="text-primary-blue"> fail coaches</span> & trainers
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Most platforms create more problems than they solve. Sound familiar?
          </p>
        </motion.div>

        {/* Problems Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {problems.map((problem, index) => (
            <motion.div
              key={index}
              className="bg-white rounded-xl p-6 hover:border-primary-blue border border-gray-200 transition-all duration-300 shadow-md"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={{ y: -5 }}
            >
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <SafeIcon icon={FiX} className="text-red-500 text-xl" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2 text-gray-900">{problem.title}</h3>
                  <p className="text-gray-600 text-sm">{problem.description}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Callout */}
        <motion.div
          className="text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <div className="bg-white border border-blue-200 rounded-2xl p-8 max-w-4xl mx-auto shadow-lg">
            <h3 className="font-serif text-2xl font-bold mb-4 text-gray-900">
              If you've faced any of these — <span className="text-primary-blue">you're not alone</span>
            </h3>
            <p className="text-lg text-gray-700 mb-6">
              My Webinar fixes all of them with our premium, AI-enhanced webinar platform
            </p>
            <motion.button
              className="bg-primary-blue hover:bg-dark-blue text-white px-8 py-4 rounded-xl font-bold text-lg transition-all hover-glow"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}
            >
              See How We Solve This
            </motion.button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Problems;